import React, { useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import axios from 'axios';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import ReactInputMask from 'react-input-mask';

const bodyShapes = ['Stick', 'Hourglass', 'Apple', 'Pear'];
const preferredStyles = [
  'Fit Right In',
  'Smoking Hot',
  'Polished Business',
  'Stylish & Trendy',
  "Mike's Dad Style",
]; // adjust label if needed

const ProfileForm = ({ customerData, setCustomerData }) => {
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm();

  const lastResetDataRef = useRef(null);

  useEffect(() => {
    // Only reset if we have meaningful customer data and it's different from last reset
    if (customerData && Object.keys(customerData).length > 0) {
      const currentDataString = JSON.stringify(customerData);
      if (currentDataString !== lastResetDataRef.current) {
        reset(customerData);
        lastResetDataRef.current = currentDataString;
      }
    }
  }, [customerData, reset]);

  const onSubmit = async values => {
    try {
      const res = await axios.post(`${process.env.REACT_APP_BASE_URL}/customer`, {
        ...customerData,
        id: customerData?.id,
        email: values.email,
        phone: values.phone,
        height: values.height,
        weight: values.weight,
        body_shape: values.body_shape,
        preferred_style: values.preferred_style,
      });
      setCustomerData({
        ...customerData,
        first_name: res?.data?.first_name,
        last_name: res?.data?.last_name,
        id: res?.data?.id,
        email: res?.data?.email,
        phone: res?.data?.phone,
        height: res?.data?.height,
        weight: res?.data?.weight,
        body_shape: res?.data?.body_shape,
        preferred_style: res?.data?.preferred_style,
        chat_sessions_available: res?.data?.chat_sessions_available,
        video_sessions_available: res?.data?.video_sessions_available,
        total_chat_sessions: res?.data?.total_chat_sessions,
        total_video_sessions: res?.data?.total_video_sessions,
      });
    } catch (e) {
      // Handle error appropriately in production
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('Failed saving profile', e);
      }
    }
  };

  return (
    <div className="profile-form-wrapper">
      <div className="text-align-center">
        <h3 className="mb-1">Profile Information:</h3>
        <p className="mt-0">Give your stylists more helpful info</p>
      </div>
      <form onSubmit={handleSubmit(onSubmit)} className="form">
        <div className="formInner">
          <div className="formInputCol">
            <input
              {...register('email', {
                required: true,
                pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              })}
              placeholder="Email Address"
            />
            {errors?.email?.type === 'required' && <p>This field is required</p>}
            {errors?.email?.type === 'pattern' && <p>Invalid Email</p>}
          </div>
          <div className="formInputCol">
            <ReactInputMask
              mask="(999) 999-9999"
              {...register('phone', { required: true })}
              placeholder="(201) 555-0123"
            />
            {errors?.phone?.type === 'required' && <p>This field is required</p>}
          </div>
        </div>
        <div className="formInner">
          <div className="formInputCol">
            <input
              {...register('height', {
                required: true,
                pattern: /^[0-9]+(\.[0-9]+)?$/,
                min: 1,
                max: 10,
              })}
              type="number"
              min="1"
              max="10"
              step="0.1"
              placeholder="Height (e.g., 5.7 ft)"
            />
            {!errors?.height && (
              <small className="input-helper">Enter your height in feet (4-10ft)</small>
            )}
            {errors?.height?.type === 'required' && <p>This field is required</p>}
            {errors?.height?.type === 'pattern' && <p>Please enter a valid height</p>}
            {errors?.height?.type === 'min' && <p>Height must be greater than 0</p>}
            {errors?.height?.type === 'max' && <p>Height cannot exceed 300 cm</p>}
          </div>
          <div className="formInputCol">
            <input
              {...register('weight', {
                required: true,
                pattern: /^[0-9]+(\.[0-9]+)?$/,
                min: 1,
                max: 1000,
              })}
              type="number"
              min="1"
              max="1000"
              step="0.1"
              placeholder="Weight (e.g., 154 lbs)"
            />
            {!errors?.weight && (
              <small className="input-helper">Enter your weight in pounds (66-2200lbs)</small>
            )}
            {errors?.weight?.type === 'required' && <p>This field is required</p>}
            {errors?.weight?.type === 'pattern' && <p>Please enter a valid weight</p>}
            {errors?.weight?.type === 'min' && <p>Weight must be greater than 0</p>}
            {errors?.weight?.type === 'max' && <p>Weight cannot exceed 1000 kg</p>}
          </div>
        </div>
        <div className="formInner">
          <div className="formInputCol">
            <FormControl fullWidth>
              <InputLabel>Body Shape</InputLabel>
              <Controller
                name="body_shape"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select {...field} label="Body Shape" value={field.value || ''}>
                    {bodyShapes.map(shape => (
                      <MenuItem key={shape} value={shape}>
                        {shape}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors?.body_shape?.type === 'required' && <p>Body Shape not selected</p>}
            </FormControl>
          </div>
          <div className="formInputCol">
            <FormControl fullWidth>
              <InputLabel>Preferred Style</InputLabel>
              <Controller
                name="preferred_style"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <Select {...field} label="Preferred Style" value={field.value || ''}>
                    {preferredStyles.map(style => (
                      <MenuItem key={style} value={style}>
                        {style}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors?.preferred_style?.type === 'required' && <p>Preferred Style not selected</p>}
            </FormControl>
          </div>
        </div>
        <div className="text-align-center">
          <button className="form-button-style" type="submit">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProfileForm;
